import React, { useState } from "react";

import pluralize from "pluralize";
import {
  IconDefinition,
  faArrowDownFromDottedLine,
  faArrowUpFromDottedLine,
  faRepeat,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";

import Button from "ui/Button";
import IndexInfo from "features/Explain/ExplainIndex";
import NodeTypeIcon from "features/Explain/ExplainNodeIcon";
import RelationInfo from "features/Explain/ExplainRelation";
import {
  useExplainSelection,
  useSelectedAnnotation,
  useSelectedAnnotationId,
  useSelectedNode,
} from "features/Explain/WithExplainNodeSelection";
import ExplainFormattedMetric from "features/Explain/ExplainFormattedMetric";
import ExplainSelectAspect from "features/Explain/ExplainSelectAspect";
import SignificantFields from "features/Explain/ExplainSignificantFields";
import {
  WithExplainAspect,
  getSubAspects,
  initialExplainAspect,
  labelAspect,
  useExplainAspect,
} from "features/Explain/WithExplainAspect";

import { AnnotatedPlan, Annotation, Node } from "types/explain";
import {
  PreparedPlan,
  extractSubplans,
  getNodeAnnotations,
  getNodeIds,
  getNodeLoops,
  isReferencedBy,
} from "utils/explain";
import { formatNumberWithSIPrefix } from "utils/format";
import Badge from "ui/Badge";
import { useCurrentPlan } from "features/Explain/WithExplainPlan";

type Props = {
  plan: AnnotatedPlan;
  databaseId: string;
  blockSize: number;
  focusable?: boolean;
};

function ExplainGridView({
  plan,
  databaseId,
  focusable = false,
  blockSize,
}: Props) {
  return (
    <WithExplainAspect initialAspect={initialExplainAspect(plan)}>
      <ExplainSelectAspect label="Show" />
      <ExplainGridViewContent
        plan={plan}
        databaseId={databaseId}
        focusable={focusable}
        blockSize={blockSize}
      />
    </WithExplainAspect>
  );
}

function ExplainGridViewContent({
  plan,
  databaseId,
  focusable,
  blockSize,
}: Props) {
  const preparedPlan = extractSubplans(plan.plan);
  const preparedAnnotatedPlan = {
    ...preparedPlan,
    annotations: plan.annotations,
  };

  const [aspect] = useExplainAspect();
  const [subAspect1, subAspect2] = getSubAspects(aspect);

  const subAspect1Label = labelAspect(aspect, subAspect1);
  const subAspect2Label = subAspect2
    ? labelAspect(aspect, subAspect2)
    : undefined;

  return (
    <table className="w-full table-fixed border-separate">
      <tbody>
        <tr>
          <th className="overflow-hidden text-ellipsis w-3/4 text-base align-top">
            Plan
          </th>
          <th className="overflow-hidden text-ellipsis text-right w-1/6 text-base align-top">
            <span className="mb-1 block text-sm text-[#666679]">
              {subAspect1Label}
            </span>
          </th>
          {subAspect2Label && (
            <th className="overflow-hidden text-ellipsis text-right w-1/6 text-base align-top">
              <span className="mb-1 block text-sm text-[#666679]">
                {subAspect2Label}
              </span>
            </th>
          )}
          <th className="w-[56px] text-base align-top">
            {/* extra info column */}
          </th>
        </tr>
        <ExplainGridLines
          plan={preparedAnnotatedPlan}
          databaseId={databaseId}
          focusable={focusable}
          blockSize={blockSize}
        />
      </tbody>
    </table>
  );
}

export type PreparedAnnotatedPlan = PreparedPlan &
  Pick<AnnotatedPlan, "annotations">;

function ExplainGridLines({
  plan,
  databaseId,
  focusable,
  blockSize,
}: {
  plan: PreparedAnnotatedPlan;
  databaseId: string;
  focusable: boolean;
  blockSize: number;
}) {
  const [, subAspect] = useExplainAspect();
  const totCols = subAspect ? 4 : 3;

  const [expanded, setExpanded] = useState(() => {
    return getDefaultExpanded(plan, focusable);
  });

  function addExpanded(ids: number[]) {
    setExpanded((currExpanded) => {
      const newExpanded = new Set(currExpanded);
      ids.forEach((id) => newExpanded.add(id));
      return newExpanded;
    });
  }

  const lines = toPlanLines(plan, expanded);
  const expandStepLines = 5;

  return lines.map((line, i) => {
    switch (line.kind) {
      case "subplan":
        return (
          <tr key={i}>
            <th
              colSpan={totCols}
              className="pt-2 overflow-hidden text-ellipsis"
            >
              {line.label}
            </th>
          </tr>
        );
      case "node":
        return (
          <ExplainNodeLine
            key={i}
            databaseId={databaseId}
            line={line}
            blockSize={blockSize}
          />
        );
      case "expand-prev": {
        function expandPrev() {
          const toExpand = getPrevToExpand(lines, i, expandStepLines);
          addExpanded(toExpand);
        }
        return (
          <tr key={i}>
            <th colSpan={totCols} className="bg-slate-100">
              <Button
                onClick={expandPrev}
                className="pl-3 w-full text-left font-normal"
                title="Expand previous"
                variant="link"
                bare
              >
                <FontAwesomeIcon icon={faArrowUpFromDottedLine} />
                {line.label && <span className="ml-1.5"> in {line.label}</span>}
              </Button>
            </th>
          </tr>
        );
      }
      case "expand-next": {
        function expandNext() {
          const toExpand = getNextToExpand(lines, i, expandStepLines);
          addExpanded(toExpand);
        }
        return (
          <tr key={i}>
            <th colSpan={totCols} className="bg-slate-100">
              <Button
                onClick={expandNext}
                className="pl-3 w-full text-left"
                title="Expand next"
                variant="link"
                bare
              >
                <FontAwesomeIcon icon={faArrowDownFromDottedLine} />
              </Button>
            </th>
          </tr>
        );
      }
      case "collapsed":
        return null;
    }
  });
}

function getDefaultExpanded(plan: PreparedAnnotatedPlan, focusable: boolean) {
  // By default, expand all nodes referenced in any annotations
  const highlights = plan.annotations.reduce((set, ann) => {
    ann.nodes.forEach((nodeRef) => set.add(nodeRef.id));
    return set;
  }, new Set<number>());
  const expandThreshold = 1;
  const result = new Set<number>();

  // collect all ids within `expandThreshold` of highlighted nodes
  [plan.root, ...plan.subPlans].forEach((subplan) => {
    const subplanIds = getNodeIds(subplan);
    const idsList = Array.from(subplanIds);
    idsList.forEach((id, idx) => {
      if (!focusable || highlights.size === 0) {
        // when there are no highlights, expand everything
        result.add(id);
        return;
      }

      const highlighted = highlights.has(id);
      if (!highlighted) {
        return;
      }

      const startExpandIdx = Math.max(idx - expandThreshold, 0);
      const endExpandIdx = Math.min(idx + expandThreshold, idsList.length - 1);
      for (
        let expandIdx = startExpandIdx;
        expandIdx <= endExpandIdx;
        expandIdx++
      ) {
        const toExpandId = idsList[expandIdx];
        result.add(toExpandId);
      }
    });
  });

  return result;
}

function getNextToExpand(lines: PlanLine[], index: number, count: number) {
  const result: number[] = [];
  let i = index;
  while (result.length < count && ++i < lines.length) {
    const line = lines[i];
    switch (line.kind) {
      case "expand-prev":
        // no more contiguous collapsed lines
        return result;
      case "collapsed":
        result.push(line.node.extra.id);
        break;
      default:
        break;
    }
  }
  return result;
}

function getPrevToExpand(lines: PlanLine[], index: number, count: number) {
  const result: number[] = [];
  let i = index;
  while (result.length < count && --i >= 0) {
    const line = lines[i];
    switch (line.kind) {
      case "expand-next":
        // no more contiguous collapsed lines
        return result;
      case "collapsed":
        result.push(line.node.extra.id);
        break;
      default:
        break;
    }
  }
  return result;
}

function ExplainNodeLine({
  databaseId,
  line,
  blockSize,
}: {
  databaseId: string;
  line: NodeLine;
  blockSize: number;
}) {
  const [selected] = useSelectedNode();
  const [annotation] = useSelectedAnnotation();
  const selection = useExplainSelection();

  const [aspect] = useExplainAspect();

  const [subAspect1, subAspect2] = getSubAspects(aspect);

  const totCols = subAspect2 ? 4 : 3;
  const node = line.node;

  const isSelected = selected?.extra.id === node.extra.id;
  const isHighlighted =
    selection === "annotation" && isReferencedBy(node.extra.id, annotation);

  return (
    <>
      <tr
        className={classNames(
          "whitespace-pre font-mono hover:bg-sky-100",
          isHighlighted && "bg-[#fef9c2] text-[#8f5204]",
        )}
      >
        <GridNodeCell line={line} />
        <td className="text-right">
          <ExplainFormattedMetric
            node={line.node}
            aspect={aspect}
            subAspect={subAspect1}
            blockSize={blockSize}
          />
        </td>
        {subAspect2 && (
          <td className="text-right pl-1">
            <ExplainFormattedMetric
              node={line.node}
              aspect={aspect}
              subAspect={subAspect2}
              blockSize={blockSize}
            />
          </td>
        )}
        <td className="pl-2">
          <NodeLoopsBadge node={line.node} />
        </td>
      </tr>
      {isSelected && (
        <tr>
          <td
            colSpan={totCols}
            className="overflow-hidden border-l-4 border-link"
          >
            <NodeDetails databaseId={databaseId} line={line} />
          </td>
        </tr>
      )}
    </>
  );
}

function NodeAnnotation({
  className,
  line,
  annotation,
}: {
  className?: string;
  line: NodeLine;
  annotation: Annotation;
}) {
  const { detail, type } = annotation;
  switch (type) {
    case "query advisor/inefficient nested loop": {
      const annNode = annotation.nodes.find(
        (nodeRef) => line.node.extra.id === nodeRef.id,
      );
      const loopRole = annNode.label;
      return (
        <Badge className={className} title={detail}>
          {loopRole}
        </Badge>
      );
    }
    case "query advisor/order by limit":
      return (
        <Badge className={className} title={detail}>
          limit/offset
        </Badge>
      );
    default:
      return (
        <Badge className={className} title={detail}>
          {type}
        </Badge>
      );
  }
}

function GridNodeCell({ line }: { line: NodeLine }) {
  const node = line.node;
  const plan = useCurrentPlan();
  const [selected, setSelected] = useSelectedNode();
  const explainSelection = useExplainSelection();
  const selectedAnnotationId = useSelectedAnnotationId();

  const nodeAnnotations = getNodeAnnotations(
    node.extra.id,
    plan.plan.annotations,
  );

  const lineDepth = node.extra.location.length - line.baseDepth;
  const neverExecuted = node["Actual Loops"] === 0;
  const isSelected = selected?.extra.id === node.extra.id;

  function handleSelected() {
    setSelected(isSelected ? undefined : node.extra.id);
  }

  const scanTarget = getScanTarget(node);

  let detail: React.ReactNode;
  if (scanTarget) {
    const detailText = `on ${scanTarget}`;
    detail = (
      <span title={detailText} className="pl-2">
        {detailText}
      </span>
    );
  }

  const cellIndent = getNodeIndent(lineDepth);

  return (
    <td
      className={classNames(
        "overflow-hidden border-l-4",
        isSelected ? "border-link" : "border-transparent",
      )}
    >
      <Button
        bare
        className="relative group w-full text-left flex"
        onClick={handleSelected}
      >
        <div className="basis-5 flex-grow-0 flex-shrink-0 grid justify-items-end items-center mr-2 text-xs text-[#999] hover:text-inherit">
          {node.extra.id}
        </div>
        <div className="text-[#CCC] absolute top-0 left-0">
          {/* draw lines from parents to children */}
          {line.openParents.map((p) => {
            const parentDepth = p.extra.location.length - line.baseDepth + 1;
            const parentOffset = getNodeIndent(
              parentDepth,
              12 /* align to center */,
            );
            const isChild = p.extra.id === line.parent.extra.id;
            const isLastChild =
              isChild && p.Plans.at(-1).extra.id === node.extra.id;
            const parentLine = isLastChild ? "└" : isChild ? "├" : "│";
            const isParallel = node.Workers?.length > 0;
            return (
              <div
                key={p.extra.id}
                className="absolute"
                style={{ left: parentOffset }}
              >
                {isParallel && (
                  <div className="absolute top-0.5 left-0.5 opacity-70">
                    {parentLine}
                  </div>
                )}
                {parentLine}
              </div>
            );
          })}
        </div>
        <div
          style={{ paddingLeft: cellIndent }}
          className="flex-shrink-0 max-w-full overflow-hidden text-ellipsis"
        >
          <NodeLabel node={node} />
        </div>
        <div className="text-gray-500 flex-shrink overflow-hidden text-ellipsis group-hover:text-inherit">
          {detail}
        </div>
        {neverExecuted && (
          <span className="pl-2 text-gray-500">(never executed)</span>
        )}
        {nodeAnnotations.map((ann, i) => {
          const someAnnotationSelected =
            explainSelection === "annotation" &&
            selectedAnnotationId != undefined;
          const isAnnSelected =
            someAnnotationSelected && selectedAnnotationId == ann.id;
          if (
            !isAnnSelected &&
            ann.nodes[0].id !== node.extra.id &&
            !isSelected
          ) {
            // Don't show "secondary" annotations (where the node is not the main one being annotated)
            // unless we're looking at the relevant annotation or we've selected this node
            return null;
          }
          const someNodeSelected =
            explainSelection === "node" && selected != undefined;

          const deemphasizeAnnotation =
            (someAnnotationSelected && !isAnnSelected) ||
            (someNodeSelected && !isSelected);
          return (
            <NodeAnnotation
              key={i}
              className={classNames(
                "!ml-2 self-center",
                deemphasizeAnnotation && "opacity-50",
              )}
              line={line}
              annotation={ann}
            />
          );
        })}
      </Button>
    </td>
  );
}

function getNodeIndent(depth: number, offset: number = 0) {
  return depth * 20 + offset + "px";
}

function NodeDetails({
  className,
  databaseId,
  line,
}: {
  className?: string;
  databaseId: string;
  line: NodeLine;
}) {
  const node = line.node;
  const lineDepth = node.extra.location.length - line.baseDepth;
  const indent = getNodeIndent(lineDepth, 48);

  return (
    <div
      className={classNames(
        "mb-2 mt-1 empty:my-0 flex flex-col gap-1",
        className,
      )}
      style={{ marginLeft: indent }}
    >
      <Subheading line={line} databaseId={databaseId} />
      <SignificantFields node={node} />
    </div>
  );
}

function Subheading({
  line,
  databaseId,
}: {
  line: NodeLine;
  databaseId: string;
}) {
  const node = line.node;
  const hasRelation = "Relation Name" in node;
  const hasIndex = "Index Name" in node;
  const hasEitherRelOrIdx = hasRelation || hasIndex;

  if (!hasEitherRelOrIdx) {
    return null;
  }

  return (
    <div className="font-mono leading-tight">
      <RelationInfo node={node} databaseId={databaseId} wrap inline />{" "}
      <IndexInfo node={node} databaseId={databaseId} wrap inline />
    </div>
  );
}

function NodeLoopsBadge({ node }: { node: Node }) {
  const [aspect] = useExplainAspect();
  // TODO: estimated rows?
  const isEstimate = aspect === "Est. Cost";
  const loopCount = getNodeLoops(node, isEstimate);
  const repeated = loopCount > 1;
  if (!repeated) {
    return null;
  }

  const repeatStr = formatNumberWithSIPrefix(loopCount);
  let tip: string;
  if (isEstimate) {
    tip = `Expected to be executed ${loopCount} times`;
  } else {
    tip = `Executed ${loopCount} times`;
    const workerCount = node["Workers"]?.length ?? 0;
    if (workerCount > 0) {
      tip += ` total using leader and ${pluralize(
        "worker",
        workerCount,
        true,
      )}`;
    }
  }

  return <NodeBadge tip={tip} icon={faRepeat} label={repeatStr} />;
}

function NodeBadge({
  label,
  tip,
  icon,
}: {
  label: string;
  tip: string;
  icon: IconDefinition;
}) {
  return (
    <span
      title={tip}
      className="border border-[#CCC] rounded-full px-2 text-xs font-bold"
    >
      <FontAwesomeIcon size="xs" className="mr-0.5" icon={icon} />
      {label}
    </span>
  );
}

function NodeLabel({ node }: { node: Node }) {
  const nodeType = node["Node Type"];
  const nodeLabel = node["Parallel Aware"] ? "Parallel " + nodeType : nodeType;
  const nodeTypeDetails = node["Scan Direction"] || node["Operation"];
  const neverExecuted = node["Actual Loops"] === 0;

  return (
    <span>
      <NodeTypeIcon className="text-gray-500 mr-1.5" nodeType={nodeType} />
      <span className={classNames(neverExecuted && "line-through")}>
        {nodeLabel}
        {nodeTypeDetails && <> ({nodeTypeDetails})</>}
      </span>
    </span>
  );
}

function getScanTarget(node: Node) {
  return "CTE Name" in node
    ? String(node["CTE Name"])
    : "Function Name" in node
    ? String(node["Function Name"]) + "(…)"
    : "Relation Name" in node
    ? String(node["Relation Name"])
    : "Index Name" in node
    ? String(node["Index Name"])
    : undefined;
}

type PlanLine =
  | NodeLine
  | CollapsedLine
  | SubplanLine
  | ExpandPrevLine
  | ExpandNextLine;

export type NodeLine = {
  kind: "node";
  baseDepth: number;
  node: Node;
  parent: Node | undefined;
  openParents: Node[];
};

type CollapsedLine = {
  kind: "collapsed";
  node: Node;
};

type SubplanLine = {
  kind: "subplan";
  label: string;
};

type ExpandPrevLine = {
  kind: "expand-prev";
  label?: string;
};

type ExpandNextLine = {
  kind: "expand-next";
};

function isExpanded(expanded: Set<number>, nodeId: number) {
  return expanded.has(nodeId);
}

function toPlanLines(
  plan: PreparedAnnotatedPlan,
  expanded: Set<number>,
): PlanLine[] {
  const lines: PlanLine[] = [];
  const rootLines = toSubplanLines(plan.root);

  let prevExpanded = undefined;
  for (const line of rootLines) {
    const currExpanded = isExpanded(expanded, line.node.extra.id);
    // we're explicitly avoiding prevExpanded == undefined here
    if (currExpanded && prevExpanded === false) {
      lines.push({ kind: "expand-prev" });
    } else if (!currExpanded && prevExpanded) {
      lines.push({ kind: "expand-next" });
    }
    if (currExpanded) {
      lines.push(line);
    } else {
      lines.push({ kind: "collapsed", node: line.node });
    }

    prevExpanded = currExpanded;
  }

  // if the main plan ends on an un-expanded node and the next subplan starts
  // expanded, add an expander to ensure we can expand the rest of the main plan
  // from the subplan direction
  if (
    !prevExpanded &&
    plan.subPlans.length > 0 &&
    isExpanded(expanded, plan.subPlans[0].extra.id)
  ) {
    lines.push({ kind: "expand-prev" });
  }

  for (const subplan of plan.subPlans) {
    const subplanName = subplan["Subplan Name"];
    const subPlanIds = getNodeIds(subplan);
    const hasExpandedLines = Array.from(subPlanIds).some((id) =>
      isExpanded(expanded, id),
    );
    if (
      prevExpanded &&
      !hasExpandedLines &&
      lines.at(-1)?.kind !== "expand-next"
    ) {
      // if no lines in this subplan are expanded, add an expand marker before
      // the subplan (unless one is already present)
      lines.push({ kind: "expand-next" });
    }
    const subplanLines = toSubplanLines(subplan);
    const startsExpanded = isExpanded(expanded, subplanLines[0].node.extra.id);
    if (startsExpanded) {
      lines.push({
        kind: "subplan",
        label: subplanName,
      });
    }
    // Add the subplan name to the first subplan expansion marker, unless the
    // first line of the subplan is already expanded
    let firstSubplanExpansion = !startsExpanded;
    prevExpanded = undefined;
    for (const line of subplanLines) {
      const currExpanded = isExpanded(expanded, line.node.extra.id);
      // Same as above: skipping prevExpanded === undefined
      if (currExpanded && prevExpanded === false) {
        const label = firstSubplanExpansion ? subplanName : null;
        firstSubplanExpansion = false;
        lines.push({ kind: "expand-prev", label });
      } else if (
        !currExpanded &&
        prevExpanded &&
        lines.at(-1)?.kind !== "expand-next"
      ) {
        lines.push({ kind: "expand-next" });
      }
      if (currExpanded) {
        lines.push(line);
      } else {
        lines.push({ kind: "collapsed", node: line.node });
      }

      prevExpanded = currExpanded;
    }
  }

  return lines;
}

function toSubplanLines(
  node: Node,
  parent?: Node | undefined,
  lines: NodeLine[] = [],
  openParents: Node[] = [],
  baseDepth: number | undefined = undefined,
) {
  baseDepth ??= node.extra.location.length;
  lines.push({
    kind: "node",
    baseDepth,
    node,
    parent,
    openParents,
  });
  const isLastChild = parent?.Plans.at(-1).extra.id === node.extra.id;
  let parents = openParents.slice();
  if (isLastChild) {
    // if we're the last child, remove our parent as an open parent
    parents = openParents.filter((p) => p.extra.id !== parent.extra.id);
  }
  parents.push(node);

  node.Plans?.forEach((child) => {
    toSubplanLines(child, node, lines, parents, baseDepth);
  });
  return lines;
}

export default ExplainGridView;
